import { baseUrl_SPB } from '../BaseUrl';

const COMPANY_JOB_ENDPOINTS = [
  '/api/v1/employmentjob/viewJobs',
  '/api/v1/employmentjob/jobsByCompany',
];

const CANDIDATE_ENDPOINTS = [
  '/api/v1/employmentjobcandidate/byJob'
];

function buildHeaders(token) {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = 'Bearer ' + token.toString();
  }

  return headers;
}

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.content)) return payload.content;
  if (payload && Array.isArray(payload.items)) return payload.items;
  if (payload && Array.isArray(payload.jobs)) return payload.jobs;
  if (payload && Array.isArray(payload.candidates)) return payload.candidates;
  return [];
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  return null;
}

async function fetchFirstSuccess(endpoints, buildRequest) {
  let lastError = null;

  for (let i = 0; i < endpoints.length; i += 1) {
    const endpoint = endpoints[i];

    try {
      const { url, options } = buildRequest(endpoint);
      const response = await fetch(url, options);

      if (!response.ok) {
        lastError = new Error(`Request failed (${response.status}) for ${endpoint}`);
        continue;
      }

      return await parseResponse(response);
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) {
    console.error(lastError);
  }

  return null;
}

export async function fetchPublicEmploymentJobs() {
  return searchPublicEmploymentJobs(null);

}

export async function searchPublicEmploymentJobs(searchCriteria) {
  const response = await fetch(`${baseUrl_SPB}/employmentjob/search`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(searchCriteria),
  });

  if (!response.ok) {
    throw new Error('Unable to search employment jobs');
  }

  const payload = await parseResponse(response);

  return normalizeList(payload);
}

export async function fetchEmploymentJobDetails(jobId) {
  const jobs = await searchPublicEmploymentJobs(null);
  return jobs.find((job) => (job?.id || job?.jobId || '') === jobId) || null;
}

export async function saveEmploymentJob(token, job) {
  const response = await fetch(`${baseUrl_SPB}/api/v1/employmentjob/save`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(job),
  });

  if (!response.ok) {
    throw new Error('Unable to save employment job');
  }

  return parseResponse(response);
}

export async function fetchCompanyEmploymentJobs(token, companyId) {
  if (!companyId) {
    return [];
  }

  const encodedCompanyId = encodeURIComponent(companyId);
  const payload = await fetchFirstSuccess(COMPANY_JOB_ENDPOINTS, (endpoint) => ({
    url: `${baseUrl_SPB}${endpoint}?companyId=${encodedCompanyId}`,
    options: {
      method: 'GET',
      headers: buildHeaders(token),
    },
  }));

  return normalizeList(payload);
}

export async function deleteEmploymentJob(token, jobId) {
  const encodedJobId = encodeURIComponent(jobId);
  const response = await fetch(`${baseUrl_SPB}/api/v1/employmentjob/deleteJob?jobId=${encodedJobId}`, {
    method: 'GET',
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw new Error('Unable to delete employment job');
  }

  return parseResponse(response);
}

export async function fetchEmploymentJobCandidates(token, jobId) {
  if (!jobId) {
    return [];
  }

  const encodedJobId = encodeURIComponent(jobId);
  const payload = await fetchFirstSuccess(CANDIDATE_ENDPOINTS, (endpoint) => ({
    url: `${baseUrl_SPB}${endpoint}?jobId=${encodedJobId}`,
    options: {
      method: 'GET',
      headers: buildHeaders(token),
    },
  }));

  return normalizeList(payload);
}

export async function applyForEmploymentJob(token, job, user) {
  const userId = user?.id || user?.userId || user?.employeeId || '';
  const jobId = job?.id || job?.jobId || '';
  const companyId = job?.companyId || user?.companyId || '';
  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const phoneNumber = user?.phoneNumber || user?.phone || '';
  const emailAddress = user?.emailAddress || user?.email || '';
  const payload = {
    id: `${jobId}-${userId}`,
    jobId,
    companyId,
    userId,
    firstName,
    lastName,
    phoneNumber,
    emailAddress,
    user: {
      ...user,
      id: userId,
      userId,
      firstName,
      lastName,
      phoneNumber,
      emailAddress,
    },
    job: {
      ...job,
      id: jobId,
      companyId,
    },
  };

  const response = await fetch(`${baseUrl_SPB}/api/v1/employmentjobcandidate/save`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('Unable to apply for employment job');
  }

  return parseResponse(response);
}

export async function addFreelanceWorkerToCompany(token, user) {
  const response = await fetch(`${baseUrl_SPB}/api/v1/fworker/addToCompany`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(user),
  });

  if (!response.ok) {
    throw new Error('Unable to add worker to company');
  }

  return parseResponse(response);
}

export async function removeFreelanceWorkerFromCompany(token, user) {
  const response = await fetch(`${baseUrl_SPB}/api/v1/fworker/removeFromCompany`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(user),
  });

  if (!response.ok) {
    throw new Error('Unable to remove worker from company');
  }

  return parseResponse(response);
}